import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  SafeAreaView,
  Platform,
} from 'react-native';
import { Feather } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { useDispatch, useSelector } from 'react-redux';
import { PlannedMeal } from '../../redux/mealPlanSlice';
import {
  selectCookingSession,
  updateEatTime,
  setScreen,
  abandonSession,
} from '../../redux/cookingSessionSlice';
import { getCookingSteps } from '../../utils/cookingSteps';
import { isDesktopWeb } from '../../utils/platformDetection';
import { Paprika, PaprikaRadius, PaprikaShadows } from '../../styles/theme';
import { CookingTopBar } from '../cooking/CookingTopBar';
import { useTranslation } from 'react-i18next';

interface PlanningViewProps {
  meal: PlannedMeal;
}

function formatMin(min: number): string {
  const h = Math.floor(((min % 1440) + 1440) % 1440 / 60);
  const m = ((min % 1440) + 1440) % 1440 % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * Pre-cooking timeline: pick the eat time, see when to start and how the
 * steps unfold. Harmonized with the cooking-mode design language (paper plan
 * card + brandTint start block + editorial timeline).
 */
const PlanningView: React.FC<PlanningViewProps> = ({ meal }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const dispatch = useDispatch();
  const session = useSelector(selectCookingSession);
  const eatTimeMin = session.eatTimeMin;
  const recipe = meal.recipe!;
  const desktop = isDesktopWeb();

  const { steps } = getCookingSteps(recipe);
  const totalMin = Math.max(
    1,
    ((recipe.preparationMinutes ?? 0) + (recipe.cookingMinutes ?? 0)) || (recipe.readyInMinutes ?? 30)
  );
  const startMin = eatTimeMin - totalMin;

  const handleAbandon = () => {
    dispatch(abandonSession());
    router.push('/(app)');
  };

  return (
    <View style={styles.container}>
      <SafeAreaView>
        <CookingTopBar
          title={recipe.title}
          servings={meal.servings}
          variant="back"
          compact={!desktop}
          onLeftPress={handleAbandon}
        />
      </SafeAreaView>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <View style={[styles.column, desktop && styles.columnDesktop]}>
          {/* Plan card: eat-time stepper + start-time block */}
          <View style={styles.planCard}>
            <View style={styles.eatRow}>
              <Text style={styles.eatLabel}>{t('cooking.atTableAt')}</Text>
              <View style={styles.stepper}>
                <TouchableOpacity
                  style={styles.stepperButton}
                  onPress={() => dispatch(updateEatTime(eatTimeMin - 15))}
                >
                  <Feather name="minus" size={17} color={Paprika.textSecondary} />
                </TouchableOpacity>
                <Text style={styles.eatTime}>{formatMin(eatTimeMin)}</Text>
                <TouchableOpacity
                  style={styles.stepperButton}
                  onPress={() => dispatch(updateEatTime(eatTimeMin + 15))}
                >
                  <Feather name="plus" size={17} color={Paprika.textSecondary} />
                </TouchableOpacity>
              </View>
            </View>
            <View style={styles.startBlock}>
              <Text style={styles.startEyebrow}>{t('cooking.cookingStart')}</Text>
              <Text style={[styles.startTime, desktop && styles.startTimeDesktop]}>
                {formatMin(startMin)}
              </Text>
              <Text style={styles.startMeta}>
                {t('cookingPlan.startMeta', {
                  minutes: totalMin,
                  steps: t('recipes.stepsCount', { count: steps.length }),
                })}
              </Text>
            </View>
          </View>

          {/* Timeline */}
          <Text style={styles.timelineEyebrow}>{t('cooking.timeline')}</Text>
          <View style={styles.timeline}>
            <View style={styles.timelineRail} />
            {steps.map((step, k) => {
              const stepTimeMin = startMin + Math.floor((k / steps.length) * totalMin);
              return (
                <View key={k} style={styles.timelineRow}>
                  <View style={styles.timelineDot} />
                  <Text style={styles.stepTimeLabel}>{formatMin(stepTimeMin)}</Text>
                  <Text style={styles.stepText} numberOfLines={2}>
                    {step.text}
                  </Text>
                </View>
              );
            })}
            <View style={styles.timelineRow}>
              <View style={[styles.timelineDot, styles.timelineDotFinal]} />
              <Text style={styles.stepTimeLabel}>{formatMin(eatTimeMin)}</Text>
              <Text style={styles.finalText}>{t('cooking.atTable')}</Text>
            </View>
          </View>
        </View>
      </ScrollView>

      {/* Glass footer CTA */}
      <SafeAreaView style={styles.footer}>
        <TouchableOpacity
          style={[styles.cta, desktop && styles.ctaDesktop]}
          onPress={() => dispatch(setScreen('cooking'))}
        >
          <Text style={styles.ctaText}>{t('cooking.startCooking')}</Text>
          <Feather name="chevron-right" size={18} color={Paprika.onPrimary} />
        </TouchableOpacity>
      </SafeAreaView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Paprika.background,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 120,
  },
  column: {
    width: '100%',
    paddingHorizontal: 16,
    paddingTop: 20,
  },
  columnDesktop: {
    maxWidth: 680,
    alignSelf: 'center',
    paddingTop: 28,
  },

  // Plan card
  planCard: {
    backgroundColor: Paprika.cardBg,
    borderRadius: PaprikaRadius.lg,
    padding: 18,
    ...PaprikaShadows.card,
  },
  eatRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 6,
    paddingBottom: 16,
  },
  eatLabel: {
    fontSize: 14,
    fontFamily: 'Inter-Medium',
    fontWeight: '500',
    color: Paprika.textSecondary,
  },
  stepper: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
  },
  stepperButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Paprika.surfaceLow,
  },
  eatTime: {
    fontFamily: 'Newsreader-Italic',
    fontStyle: 'italic',
    fontSize: 26,
    color: Paprika.textPrimary,
    fontVariant: ['tabular-nums'],
    minWidth: 74,
    textAlign: 'center',
  },
  startBlock: {
    backgroundColor: Paprika.brandTint,
    borderWidth: 1,
    borderColor: Paprika.brandSoft,
    borderRadius: PaprikaRadius.md,
    paddingTop: 20,
    paddingBottom: 18,
    paddingHorizontal: 16,
    alignItems: 'center',
  },
  startEyebrow: {
    fontSize: 11,
    fontFamily: 'Inter-SemiBold',
    fontWeight: '600',
    letterSpacing: 1.3,
    textTransform: 'uppercase',
    color: Paprika.accent,
    marginBottom: 6,
  },
  startTime: {
    fontFamily: 'Newsreader-Italic',
    fontStyle: 'italic',
    fontSize: 44,
    lineHeight: 48,
    color: Paprika.brandDeep,
    fontVariant: ['tabular-nums'],
  },
  startTimeDesktop: {
    fontSize: 52,
    lineHeight: 56,
  },
  startMeta: {
    fontSize: 12.5,
    fontFamily: 'Inter-Regular',
    color: Paprika.textSecondary,
    marginTop: 6,
  },

  // Timeline
  timelineEyebrow: {
    fontSize: 11,
    fontFamily: 'Inter-SemiBold',
    fontWeight: '600',
    letterSpacing: 1.3,
    textTransform: 'uppercase',
    color: Paprika.textMuted,
    marginTop: 30,
    marginBottom: 16,
    paddingLeft: 4,
  },
  timeline: {
    position: 'relative',
    paddingLeft: 4,
  },
  timelineRail: {
    position: 'absolute',
    left: 7.5,
    top: 14,
    bottom: 14,
    width: 1.5,
    backgroundColor: Paprika.hairline,
  },
  timelineRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 14,
    paddingVertical: 9,
  },
  timelineDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: Paprika.border,
    marginTop: 5,
    borderWidth: 2,
    borderColor: Paprika.background,
  },
  timelineDotFinal: {
    backgroundColor: Paprika.accent,
  },
  stepTimeLabel: {
    width: 44,
    fontSize: 13,
    fontFamily: 'Inter-SemiBold',
    fontWeight: '600',
    color: Paprika.accent,
    fontVariant: ['tabular-nums'],
    paddingTop: 1,
  },
  stepText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
    fontFamily: 'Inter-Regular',
    color: Paprika.textPrimary,
  },
  finalText: {
    flex: 1,
    fontFamily: 'Newsreader-Italic',
    fontStyle: 'italic',
    fontSize: 19,
    lineHeight: 22,
    color: Paprika.textPrimary,
  },

  // Footer
  footer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(251, 249, 246, 0.85)',
    ...(Platform.OS === 'web' ? ({ backdropFilter: 'blur(20px)' } as any) : null),
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: 16,
  },
  cta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 9,
    height: 52,
    borderRadius: PaprikaRadius.lg,
    backgroundColor: Paprika.accent,
    paddingHorizontal: 26,
    alignSelf: 'stretch',
  },
  ctaDesktop: {
    alignSelf: 'center',
    minWidth: 280,
  },
  ctaText: {
    fontSize: 15,
    fontFamily: 'Inter-SemiBold',
    fontWeight: '600',
    color: Paprika.onPrimary,
  },
});

export default PlanningView;
